import React from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import HeaderNav from "../components/HeaderNav";
import { Activity, Upload, BarChart3, Target, FileCheck2, ShieldCheck } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1686061592689-312bbfb5c055?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwyfHxleGVjdXRpdmUlMjBwcm9mZXNzaW9uYWwlMjByZXN1bWUlMjBjYXJlZXIlMjBhbmFseXRpY3MlMjBkYXNoYm9hcmR8ZW58MHx8fHwxNzkwNDQzNjkxfDA&ixlib=rb-4.1.0&q=85";

const STEPS = [
  { icon: Upload, t: "Upload", d: "PDF or DOCX. JD optional." },
  { icon: BarChart3, t: "Analyze", d: "Explainable, evidence-grounded." },
  { icon: Target, t: "Target", d: "Top roles you can credibly reach." },
  { icon: FileCheck2, t: "Regenerate", d: "One-click role-targeted resume." },
];

const Landing = () => {
  const { login, user } = useAuth();
  return (
    <div className="min-h-screen bg-slate-50 text-slate-800">
      <HeaderNav />
      <main className="max-w-6xl mx-auto px-4 sm:px-6">
        <section className="grid lg:grid-cols-2 gap-10 items-center py-14 md:py-20">
          <div>
            <span className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-widest text-sky-700 bg-sky-50 border border-sky-200 rounded-full px-3 py-1">
              <Activity className="w-3.5 h-3.5" /> Career-os for you
            </span>
            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.05]">
              Know why you're not <span className="text-sky-600">getting shortlisted</span>.
            </h1>
            <p className="mt-5 text-base sm:text-lg text-slate-500 leading-relaxed max-w-xl">
              For experienced professionals (5–20 yrs). Upload your resume for an explainable Career Intelligence Dashboard — then regenerate a role-targeted resume in one click. No fabrication, ever.
            </p>
            <div className="mt-8 flex items-center gap-3">
              <Button onClick={login} className="h-12 px-6 bg-sky-600 hover:bg-sky-500 text-white font-semibold text-base" data-testid="google-auth-button">
                Sign in
              </Button>
              {user && <a href="/app" className="text-sm text-sky-600 hover:text-sky-700">Go to dashboard →</a>}
            </div>
            <p className="mt-4 text-xs text-slate-500 flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> Scores are heuristic guidance indicators, not guaranteed ATS metrics.</p>
          </div>
          <div className="relative">
            <div className="absolute -inset-4 bg-sky-50 blur-3xl rounded-full" />
            <img src={HERO} alt="Career analytics" className="relative rounded-2xl border border-slate-200 shadow-2xl object-cover w-full h-[200px] sm:h-[280px] lg:h-[340px]" />
          </div>
        </section>

        <section className="pb-20 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={s.t} className="rounded-2xl bg-white border border-slate-200 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_10px_30px_-24px_rgba(15,23,42,0.22)] p-5">
                <Icon className="w-6 h-6 text-sky-600" />
                <p className="mt-3 font-bold text-slate-900">{i + 1}. {s.t}</p>
                <p className="text-sm text-slate-500 mt-1">{s.d}</p>
              </div>
            );
          })}
        </section>

        <footer className="pb-10 border-t border-slate-200 pt-6 flex flex-col sm:flex-row items-center justify-between gap-2" data-testid="site-footer">
          <p className="text-sm text-slate-500">© {new Date().getFullYear()} tomorrowsjob — Career-os for you</p>
          <p className="text-sm text-slate-500">
            Powered by <a href="https://klickapps.io" target="_blank" rel="noopener noreferrer" className="text-sky-600 hover:text-sky-700 transition-colors font-medium" data-testid="powered-by-link">klickapps.io</a>
          </p>
        </footer>
      </main>
    </div>
  );
};

export default Landing;
